// Editorial "writing assistant" for the Story Builder. Pure client-side quality
// checks over the current item's article + structured data. It does NOT generate
// prose — it only flags consistency/coverage issues to strengthen the record.

const DOMAIN_STOP = new Set([
  "elvis", "presley", "aloha", "hawaii", "hawaiian", "collection", "ticket",
  "tickets", "concert", "satellite", "graceland", "1972", "1973", "original",
  "from", "the", "and", "with", "display", "documents", "document", "artifact",
  "artifacts", "record", "records",
]);

const stripInline = (s) =>
  String(s || "")
    .replace(/\[\^c:[^\]]+\]/g, " ")
    .replace(/\[([^\]]+)\]\([^)]+\)/g, "$1")
    .replace(/[#>*_`~-]/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const wordCount = (s) => stripInline(s).split(/\s+/).filter(Boolean).length;

export function analyzeArticle({ markdown = "", citations = [], item = {}, allItems = [], events = [] }) {
  const issues = [];
  const text = markdown || "";

  // --- Sections ---
  const lines = text.split("\n");
  const sections = [];
  let cur = { title: "(intro)", body: [] };
  for (const ln of lines) {
    const h = /^(#{1,3})\s+(.*)$/.exec(ln);
    if (h) { sections.push(cur); cur = { title: h[2].trim(), body: [] }; }
    else cur.body.push(ln);
  }
  sections.push(cur);

  const contentSections = sections.filter((s) => s.title !== "(intro)");

  // Short sections (<200 words), excluding the auto Notes
  contentSections.forEach((s) => {
    if (s.title.toLowerCase() === "notes") return;
    const wc = wordCount(s.body.join(" "));
    if (wc > 0 && wc < 200) {
      issues.push({ type: "short_section", severity: "info", message: `Section “${s.title}” is short (${wc} words) — consider expanding toward museum depth.` });
    }
  });

  // --- Paragraph-level checks ---
  const paras = text
    .split(/\n\s*\n/)
    .map((p) => p.trim())
    .filter((p) => p && !/^(#{1,3})\s+/.test(p));

  // Uncited factual statements (contain a year or a quantity but no citation marker)
  let uncited = 0;
  paras.forEach((p) => {
    if (/\[\^c:/.test(p)) return;
    if (/\*\(.*\)\*/.test(p)) return; // outline placeholder text
    const hasFact = /\b(18|19|20)\d{2}\b/.test(p) || /\$\s?\d/.test(p) || /\b\d{2,}\b/.test(p);
    if (hasFact && uncited < 8) {
      uncited += 1;
      issues.push({ type: "uncited", severity: "warning", message: `Possible uncited statement: “${stripInline(p).slice(0, 90)}…” — add a source.` });
    }
  });

  // Repeated paragraphs
  const seen = new Set();
  paras.forEach((p) => {
    const norm = stripInline(p).toLowerCase();
    if (norm.length < 40) return;
    if (seen.has(norm)) {
      issues.push({ type: "repeat", severity: "warning", message: `Repeated paragraph detected: “${norm.slice(0, 70)}…”` });
    } else {
      seen.add(norm);
    }
  });

  // Date conflicts vs linked timeline events
  const artYears = [...text.matchAll(/\b(18|19|20)\d{2}\b/g)].map((m) => Number(m[0]));
  const evYears = [];
  events.forEach((e) => {
    const mm = String(e.date_sort || e.date_label || "").match(/\b(18|19|20)\d{2}\b/g);
    if (mm) mm.forEach((y) => evYears.push(Number(y)));
  });
  if (evYears.length && artYears.length) {
    const lo = Math.min(...evYears);
    const hi = Math.max(...evYears);
    const outliers = [...new Set(artYears.filter((y) => y < lo || y > hi))];
    outliers.forEach((y) => {
      issues.push({ type: "date_conflict", severity: "warning", message: `Year ${y} in the article falls outside the linked timeline range (${lo}–${hi}) — verify consistency.` });
    });
  }

  // Cross-link suggestions (other items whose distinctive title tokens appear in the article)
  const relatedSet = new Set(item.related_item_ids || []);
  const lowerText = stripInline(text).toLowerCase();
  let crosslinks = 0;
  for (const o of allItems) {
    if (crosslinks >= 5) break;
    if (o.id === item.id || relatedSet.has(o.id)) continue;
    const toks = (o.title || "").toLowerCase().split(/[^a-z0-9]+/).filter((w) => w.length >= 5 && !DOMAIN_STOP.has(w));
    if (toks.some((t) => lowerText.includes(t))) {
      crosslinks += 1;
      issues.push({ type: "crosslink", severity: "info", message: `Consider linking related item: “${o.title}”.` });
    }
  }

  const totalWords = wordCount(text);
  if (text.trim() && totalWords < 200) {
    issues.push({ type: "length", severity: "info", message: `Article is brief (${totalWords} words). Museum-style entries typically run 1,000–5,000 words.` });
  }

  return {
    stats: { words: totalWords, sections: contentSections.length, citationsAttached: citations.length },
    issues,
  };
}
